<template>
    <div class="content-container">
        <div class="content h-auto">
            <div class="container">
                <div class="row">

                    <!-- Title -->
                    <div class="col-12">
                        <div class="title">
                            <h1>
                              <template v-if="isBookingInStore">{{$t('bp.CustomerDetails.inStoreBookingTitle')}}</template>
                              <template v-else>{{$t('bp.CustomerDetails.title')}}</template>
                            </h1>
                        </div>
                    </div>

                    <!-- Form -->
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>{{$t('bp.CustomerDetails.firstName')}} <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" v-model="customer.firstName" @keyup="keyMonitor('first_name')" :class="{'is-invalid': errors['first_name']}">
                            <p class="help-block text-danger text-sm" v-if="errors['first_name']">* {{ errors['first_name'][0] }}</p>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>{{$t('bp.CustomerDetails.lastName')}} <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" v-model="customer.lastName" @keyup="keyMonitor('last_name')" :class="{'is-invalid': errors['last_name']}">
                            <p class="help-block text-danger text-sm" v-if="errors['last_name']">* {{ errors['last_name'][0] }}</p>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="form-group">
                            <label>{{$t('bp.CustomerDetails.email')}} <span class="text-danger">*</span></label>
                            <input type="email" class="form-control" v-model="customer.email" @keyup="keyMonitor('email')" :class="{'is-invalid': errors['email']}">
                            <p class="help-block text-danger text-sm" v-if="errors['email']">* {{ errors['email'][0] }}</p>
                            <small class="form-text text-muted">{{$t('bp.CustomerDetails.emailNote')}}</small>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>{{$t('bp.CustomerDetails.phoneNumber')}} <span class="text-danger">*</span></label>
                            <input type="tel" class="form-control" v-model="customer.phoneNumber" @keyup="keyMonitor('phone_number')" :class="{'is-invalid': errors['phone_number']}">
                            <p class="help-block text-danger text-sm" v-if="errors['phone_number']">* {{ errors['phone_number'][0] }}</p>
                            <small class="form-text text-muted">{{$t('bp.CustomerDetails.phoneNumberNote')}}</small>
                        </div>
                    </div>

                    <!-- Contact Consent -->
                    <div class="col-12">
                        <div class="form-group mb-2">
                            <label>{{$t('bp.CustomerDetails.consentLabel')}}</label>
                            <label class="custom-control custom-radio">
                                <input type="checkbox" class="custom-control-input" v-model="customer.emailConsent">
                                <span class="custom-control-indicator"></span>
                                <span class="custom-control-description ml-1 font-weight-normal">
                                    {{$t('bp.CustomerDetails.consentText')}}
                                    <template v-if="countryCodeSetting === 'DE'">
                                        <br />
                                        {{$t('bp.CustomerDetails.consentText2')}}
                                        <br /><br />
                                        {{$t('bp.CustomerDetails.consentText3')}}
                                    </template>
                                    <ul class="mt-2 text-sm" v-if="countryCodeSetting !== 'DE'">
                                        <li>{{$t('bp.CustomerDetails.consent.li1')}}</li>
                                        <li>{{$t('bp.CustomerDetails.consent.li2')}}</li>
                                        <li>{{$t('bp.CustomerDetails.consent.li3.p1')}} <router-link :to="{name: 'Privacy'}"  target="_blank">{{$t('bp.privacyPolicy')}}</router-link></li>
                                    </ul>
                                </span>
                            </label>
                        </div>
                    </div>
                    <div class="col-12">
                        <template v-if="countryCodeSetting !== 'DE'">
                            <p class="text-sm">
                                {{$t('bp.CustomerDetails.policy.p1')}} <router-link :to="{name: 'Terms'}" target="_blank">{{$t('bp.policy.p2')}}</router-link> {{$t('bp.policy.p3')}} <router-link :to="{name: 'Privacy'}" target="_blank">{{$t('bp.privacyPolicy')}}</router-link><template v-if="countryCodeSetting === 'DE'">{{$t('bp.policy.p4')}}</template>
                            </p>
                        </template>
                        <template v-if="countryCodeSetting === 'DE'">
                            <label class="custom-control custom-radio">
                                <input type="checkbox" class="custom-control-input" v-model="consentGiven">
                                <span class="custom-control-indicator"></span>
                                <span class="custom-control-description ml-1 font-weight-normal">
                                    {{$t('bp.CustomerDetails.policy.p1')}} <router-link :to="{name: 'Terms'}" target="_blank">{{$t('bp.policy.p2')}}</router-link> {{$t('bp.policy.p3')}} <router-link :to="{name: 'Privacy'}" target="_blank">{{$t('bp.privacyPolicy')}}</router-link><template v-if="countryCodeSetting === 'DE'">{{$t('bp.policy.p4')}}</template>
                                </span>
                            </label>
                        </template>
                    </div>
                    <!-- Footer -->
                    <footer class="footer">
                        <div class="container">
                            <router-link :to="{ name: previousStep }" class="btn btn-outline-secondary back-btn btn-lg"><i class="fa fa-chevron-left text-sm mr-3"></i> {{$t('bp.backButton')}}</router-link>

                            <button v-if="!checkingDetails" @click="initiateSetCustomerDetails()" class="btn btn-primary btn-lg continue-btn icon-right ml-auto" :disabled="!canContinue">{{$t('bp.nextButton')}} <span><i class="fa fa-chevron-right ml-3 text-sm"></i></span></button>
                            <button v-if="checkingDetails" @click="initiateSetCustomerDetails()" class="btn btn-primary btn-lg continue-btn icon-right ml-auto" disabled><span><i class="fa fa-pulse fa-spinner"></i></span> {{$t('bp.CustomerDetails.checkingButton')}}</button>
                        </div>
                    </footer>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import {mapActions, mapGetters} from "vuex";

    export default {
        name: 'CustomerDetails',

        mounted() {
            this.checkCanBeOnPage();

            this.setExistingDetails();
        },

        data() {
            return {
                checkingDetails: false,
                customer: {
                    email: null,
                    emailConsent: false,
                    firstName: null,
                    lastName: null,
                    phoneNumber: null
                },
                errors: [],
                consentGiven: false
            }
        },

        computed: {
            ...mapGetters([
                "booking",
                "carrier",
                "carrierDetails",
                "bookingIsDropOff",
                "isBookingInStore",
                "storeCode",
                "countryCodeSetting"
            ]),

            canContinue() {
                if (this.countryCodeSetting === 'DE' && !this.consentGiven) {
                    return false;
                }
                return (
                    this.customer.email &&
                    this.customer.firstName &&
                    this.customer.lastName &&
                    this.customer.phoneNumber
                );
            },

            pageTitle() {
                if (this.isBookingInStore) {
                    return "Customer details";
                }

                return "Your details";
            },

            previousStep() {
                if(this.bookingIsDropOff) {
                    return 'repairDates';
                }

                return 'repairLocation';
            }
        },

        methods: {
            ...mapActions(['checkBookingIsComplete', 'setCustomerDetails']),

            checkCanBeOnPage() {
                this.checkBookingIsComplete().then(() => {
                    if (!this.booking.date) {
                        this.$router.push({name: 'repairDates'});
                    }
                });
            },

            initiateSetCustomerDetails() {
                if (!this.canContinue) return;

                this.checkingDetails = true;

                this.setCustomerDetails({
                    carrier: this.carrier,
                    store_code: this.storeCode,
                    first_name: this.customer.firstName,
                    last_name: this.customer.lastName,
                    email: this.customer.email,
                    email_consent: this.customer.emailConsent,
                    phone_number: this.customer.phoneNumber
                }).then(() => {
                    this.checkingDetails = false;
                    this.$router.push({name: 'checkout'});
                }).catch (error => {
                    this.errors = error.response.data.messages;
                    this.checkingDetails = false;
                });
            },

            setExistingDetails() {
                this.customer.email = this.booking.email;
                this.customer.emailConsent = this.booking.email_consent;
                this.customer.firstName = this.booking.first_name;
                this.customer.lastName = this.booking.last_name;
                this.customer.phoneNumber = this.booking.phone_number;
            },

            keyMonitor(errorIndex) {
                Vue.delete(this.errors, errorIndex);
            }
        }

    }
</script>

<style scoped>

</style>